<template>
  <div style="border: 1px solid #ccc; z-index: 100">
    <LoadingMask :visible="showUploadLoading" :text="uploadLoadingText" />
    <Toolbar style="border-bottom: 1px solid #ccc" :editor="editor" :defaultConfig="toolbarConfig" :mode="mode" />
    <Editor
      :style="{ height: editorHeight, overflowY: 'hidden' }"
      v-model="html"
      :defaultConfig="editorConfig"
      :mode="mode"
      @onCreated="onCreated"
      @onChange="onChange"
      @customPaste="handleCustomPaste"
    />
  </div>
</template>

<style src="@wangeditor/editor/dist/css/style.css"></style>
<style>
/* 修复 wangEditor 弹窗按钮文字居中问题 */
.w-e-modal button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>

<script>
import Vue from 'vue'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import COS from 'cos-js-sdk-v5'
import { ossSignature } from '@/api/system/user'
import moment from 'moment'
import LoadingMask from '@/components/LoadingMask.vue'

export default Vue.extend({
  name: 'WangEditorV5',
  components: { Editor, Toolbar, LoadingMask },
  props: {
    value: {
      type: String,
      default: '',
    },
    height: {
      type: Number,
      default: 300,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'default',
    },
    imageSize: {
      type: Number,
      default: 5,
    },
    videoSize: {
      type: Number,
      default: 100,
    },
  },
  data() {
    const that = this
    return {
      editor: null,
      html: '',
      showUploadLoading: false,
      uploadLoadingText: '正在上传文件',
      toolbarConfig: {},
      editorConfig: {
        placeholder: '请输入内容...',
        readOnly: this.readOnly,
        MENU_CONF: {
          uploadImage: {
            maxFileSize: this.imageSize * 1024 * 1024,
            customUpload: (file, insertFn) => {
              that.uploadFile(file, insertFn, 'image')
            },
          },
          uploadVideo: {
            maxFileSize: this.videoSize * 1024 * 1024,
            customUpload: (file, insertFn) => {
              that.uploadFile(file, insertFn, 'video')
            },
          },
        },
      },
    }
  },
  computed: {
    editorHeight() {
      return this.height + 'px'
    },
  },
  watch: {
    value: {
      handler(val) {
        if (val !== this.html) {
          this.html = val === null ? '' : val
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleCustomPaste(editor, event) {
      try {
        const html = event.clipboardData.getData('text/html')
        if (html && html.trim()) {
          event.preventDefault()
          const processedHtml = this.processHtmlForPaste(html)
          editor.dangerouslyInsertHtml(processedHtml)
          return false
        }
        const text = event.clipboardData.getData('text/plain')
        if (text && !html) {
          return true
        }
        return false
      } catch (error) {
        console.warn('粘贴处理出错:', error)
        return true
      }
    },
    processHtmlForPaste(html) {
      if (!html || !html.trim()) {
        return ''
      }

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html

      const styleMap = {}
      const styleTags = tempDiv.querySelectorAll('style')
      styleTags.forEach((styleTag) => {
        const cssText = styleTag.textContent || ''
        const regex = /([.#]?[a-zA-Z0-9_-]+)\s*\{([^}]+)\}/g
        let match
        while ((match = regex.exec(cssText)) !== null) {
          const selector = match[1]
          const styles = match[2].trim()
          styleMap[selector] = styles
        }
        styleTag.remove()
      })

      const removeTags = tempDiv.querySelectorAll('meta, title, script, link, base')
      removeTags.forEach((tag) => tag.remove())

      const allElements = tempDiv.querySelectorAll('*')
      allElements.forEach((el) => {
        let inlineStyles = el.getAttribute('style') || ''

        if (el.className && typeof el.className === 'string') {
          const classes = el.className.split(/\s+/).filter((c) => c)
          classes.forEach((cls) => {
            const classKey = '.' + cls
            if (styleMap[classKey]) {
              inlineStyles += ';' + styleMap[classKey]
            }
          })
        }

        if (el.id) {
          const idKey = '#' + el.id
          if (styleMap[idKey]) {
            inlineStyles += ';' + styleMap[idKey]
          }
        }

        const tagKey = el.tagName.toLowerCase()
        if (styleMap[tagKey]) {
          inlineStyles += ';' + styleMap[tagKey]
        }

        if (inlineStyles) {
          el.setAttribute('style', inlineStyles.replace(/^;/, '').replace(/;;+/g, ';'))
        }
      })

      const body = tempDiv.querySelector('body')
      let result = body ? body.innerHTML : tempDiv.innerHTML

      if (!result || !result.trim()) {
        result = tempDiv.textContent || html
      }

      return result
    },
    generateCosKey(name, ext) {
      var ymd = moment().format('YYYYMMDD_HHmmss_')
      var r = ('000000' + Math.random() * 1000000).slice(-6)
      var cosKey = name + `/IMG_${ymd}_${r}.${ext}`
      return cosKey
    },
    compressImage(file, targetSizeMB = 20, maxQuality = 0.85) {
      return new Promise((resolve, reject) => {
        const fileSizeMB = file.size / (1024 * 1024)

        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = function (e) {
          const img = new Image()
          img.src = e.target.result
          img.onload = function () {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')

            canvas.width = img.width
            canvas.height = img.height
            ctx.drawImage(img, 0, 0)

            let quality = maxQuality
            if (fileSizeMB > 20) {
              quality = 0.6
            } else if (fileSizeMB > 10) {
              quality = 0.75
            } else if (fileSizeMB > 5) {
              quality = 0.85
            } else {
              quality = 0.9
            }

            const tryCompress = (compressQuality) => {
              return new Promise((resolve) => {
                canvas.toBlob(
                  function (blob) {
                    if (blob) {
                      const blobSizeMB = blob.size / (1024 * 1024)
                      if (blobSizeMB <= targetSizeMB || compressQuality <= 0.5) {
                        const compressedFile = new File([blob], file.name, {
                          type: file.type || 'image/jpeg',
                          lastModified: Date.now(),
                        })
                        resolve(compressedFile)
                      } else if (compressQuality > 0.5) {
                        tryCompress(compressQuality - 0.1).then(resolve)
                      } else {
                        const compressedFile = new File([blob], file.name, {
                          type: file.type || 'image/jpeg',
                          lastModified: Date.now(),
                        })
                        resolve(compressedFile)
                      }
                    } else {
                      reject(new Error('图片压缩失败'))
                    }
                  },
                  file.type || 'image/jpeg',
                  compressQuality
                )
              })
            }

            tryCompress(quality)
              .then((compressedFile) => {
                resolve(compressedFile)
              })
              .catch((error) => {
                reject(error)
              })
          }
          img.onerror = function () {
            reject(new Error('图片加载失败'))
          }
        }
        reader.onerror = function () {
          reject(new Error('文件读取失败'))
        }
      })
    },
    uploadFile(file, insertFn, type) {
      const that = this
      var lastIndex = file.name.lastIndexOf('.')
      var extName = lastIndex > -1 ? file.name.slice(lastIndex + 1) : ''
      const cosKey = this.generateCosKey(this.$cosUrl, extName)

      const shouldCompress = type === 'image'

      const doUpload = function (uploadFile) {
        const uploadFileSizeMB = uploadFile.size / (1024 * 1024)
        const usePicOperations = type === 'image' && uploadFileSizeMB <= 20

        ossSignature()
          .then((res) => {
            res = res.data
            const cos = new COS({
              SecretId: res.tmpSecretId,
              SecretKey: res.tmpSecretKey,
              SecurityToken: res.sessionToken,
              StartTime: res.startTime,
              ExpiredTime: res.expiredTime,
            })

            const obj = {
              Bucket: that.$Bucket,
              Region: 'ap-beijing',
              Key: cosKey,
              Body: uploadFile,
              SliceSize: 1024 * 1024 * 1,
              onProgress: function (progressData) {
                if (progressData.percent) {
                  console.log('上传进度:', Math.round(progressData.percent * 100) + '%')
                }
              },
            }

            if (usePicOperations) {
              obj.Headers = {
                'Pic-Operations': JSON.stringify({
                  is_pic_info: 1,
                  rules: [
                    {
                      fileid: cosKey,
                      rule: 'imageMogr2/quality/33',
                    },
                  ],
                }),
              }
            }

            cos.putObject(obj, function (err, data) {
              that.showUploadLoading = false
              if (err) {
                console.log('上传失败', err)
                let errorMsg = '上传失败'
                if (err.code === 'EntityTooLarge') {
                  errorMsg = '文件过大，请压缩后重试或使用较小的图片'
                } else if (err.message) {
                  errorMsg = '上传失败: ' + err.message
                }
                that.$message.error(errorMsg)
              } else {
                const url = that.$url + cosKey
                if (type === 'image') {
                  insertFn(url, '', url)
                } else if (type === 'video') {
                  insertFn(url, '')
                }
              }
            })
          })
          .catch((error) => {
            that.showUploadLoading = false
            console.log('获取签名失败', error)
            that.$message.error('上传失败: 获取签名失败')
          })
      }

      that.showUploadLoading = true
      that.uploadLoadingText = '正在上传文件'

      if (shouldCompress) {
        that.uploadLoadingText = '正在优化图片'
        that
          .compressImage(file, 20)
          .then((compressedFile) => {
            that.uploadLoadingText = '正在上传文件'
            doUpload(compressedFile)
          })
          .catch((error) => {
            console.log('压缩失败', error)
            that.uploadLoadingText = '正在上传文件'
            doUpload(file)
          })
      } else {
        doUpload(file)
      }
    },
    onCreated(editor) {
      this.editor = Object.seal(editor)
      if (this.readOnly) {
        editor.disable()
      }
    },
    onChange(editor) {
      const html = editor.getHtml()
      if (html !== this.value) {
        this.$emit('input', html)
      }
    },
    getEditor() {
      return this.editor
    },
    getHtml() {
      return this.editor ? this.editor.getHtml() : ''
    },
    getText() {
      return this.editor ? this.editor.getText() : ''
    },
    setHtml(html) {
      this.html = html
    },
    clear() {
      this.html = ''
      if (this.editor) {
        this.editor.clear()
      }
    },
  },
  beforeDestroy() {
    const editor = this.editor
    if (editor == null) return
    editor.destroy()
  },
})
</script>
